import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { ELEMENT_STATE } from '@/constants/element/constants';
import { INTERACTION_STATE } from '@/constants/interaction/constants';
import EcsConversation from '@/components/ecs-conversation/EcsConversation';
import EcsInteraction from '@/components/ecs-interaction/EcsInteraction';
import EcsInteractionMixin from '@/mixins/ecs-interaction-mixin/EcsInteractionMixin';
import InteractionService from '@/services/interactionService';
import { STATUS_SUCCESS } from '@/constants/appConstants';
import { splitCamelCasedWords } from '@/utils';
import EcsSoftPhoneMixin from '@/components/ecs-soft-phone/EcsSoftPhoneMixin';

const SNACKBAR_MESSAGE_EVENT = 'snackbar-message';

export default {
  name: 'EcsInteractionActionsMixin',

  mixins: [EcsInteractionMixin, EcsSoftPhoneMixin],

  sockets: {
    INTERACTION_TRANSFERRED_EVENT(event) {
      const interaction = new EcsInteraction(event);
      if (interaction.isEmail) {
        const successMessage = this.$t('snackbar.successfullyTransferredTo', {
          name: interaction.transfer.handlerName || interaction.transfer.endPoint
        });

        this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
          message: successMessage,
          type: 'success'
        });
      }
    }
  },

  computed: {
    ...mapGetters('BUSINESS_PROCESSES', ['availableBusinessProcesses']),

    ...mapGetters('CONTACTS', ['getContact', 'getCrmContact']),

    ...mapGetters('ELEMENTS', ['getElementsForInteraction', 'getElementIdsByInteractionId']),

    ...mapGetters('CONVERSATIONS', ['getConversationForElement', 'getConversationsForInteraction']),

    ...mapGetters('INTERACTIONS', ['getActiveTelephonyInteractions']),

    ...mapGetters('INTERACTIONS/emails', ['getEmailByInteractionId']),

    ...mapGetters('REFERENCES', ['isSmtpEnabled']),

    ...mapGetters('WINDOWS', ['getInteractionWindow']),

    ...mapGetters('AGENT', ['agentState']),

    ...mapGetters('ORGANIZATION', ['postponeOptions', 'organizationData', 'getOrganizationBPbyId']),

    email() {
      if (!Object.keys(this.interaction).length) {
        return {};
      }
      const email = this.getEmailByInteractionId(this.interaction.interactionId);
      return email || {};
    },

    smsConversationHasMessages() {
      if (!this.interaction.isSms) {
        return false;
      }
      const conversations = this.getConversationsForInteraction({ interactionId: this.interaction.interactionId });
      return conversations[0] && conversations[0].messages.length < 2;
    },

    telephonyInteractionExists() {
      return this.getActiveTelephonyInteractions.length > 0;
    },

    options() {
      return {
        hold: {
          icon: 'mdi-pause',
          label: 'buttons.hold',
          action: this.hold,
          waitsFor: [ELEMENT_STATE.diverted, ELEMENT_STATE.conferencedDiverted]
        },
        resume: {
          icon: 'mdi-play-circle',
          label: 'buttons.resume',
          action: this.resume,
          waitsFor: [ELEMENT_STATE.active, ELEMENT_STATE.conferenced]
        },
        consult: {
          icon: 'mdi-account-plus',
          label: 'buttons.consult',
          action: this.openConsultDialog
        },
        conference: {
          icon: 'mdi-call-merge',
          label: this.interaction.isChat ? 'buttons.startChatConference' : 'buttons.startCallConference',
          action: this.conference,
          waitsFor: [ELEMENT_STATE.conferenced]
        },
        splitConference: {
          icon: 'mdi-call-split',
          label: 'buttons.splitConference',
          action: this.splitConference,
          waitsFor: [ELEMENT_STATE.diverted, ELEMENT_STATE.conferencedDiverted]
        },
        replyEmail: {
          icon: 'mdi-reply',
          label: 'buttons.reply',
          action: this.replyEmail,
          waitsFor: [ELEMENT_STATE.created]
        },
        transfer: {
          icon: 'mdi-forward',
          label: 'buttons.transfer',
          action: this.openTransferDialog,
          disabled: this.smsConversationHasMessages,
          waitsFor: ['INTERACTION_TRANSFERRED_EVENT']
        },
        transferAttended: {
          icon: 'mdi-phone-forward',
          label: 'buttons.transfer',
          action: this.transferAttended,
          waitsFor: ['INTERACTION_TRANSFERRED_EVENT']
        },
        transferToAutoVMService: {
          icon: 'mdi-voicemail',
          label: 'buttons.transferToAutoVM',
          action: this.transferToAutoVM,
          waitsFor: ['INTERACTION_TRANSFERRED_EVENT']
        },
        transferToSurvey: {
          icon: 'mdi-format-list-checks',
          label: 'buttons.transferToSurvey',
          action: this.transferToSurvey,
          waitsFor: ['INTERACTION_TRANSFERRED_EVENT']
        },
        playMediaFile: {
          icon: 'mdi-playlist-play',
          label: 'buttons.playMediaFile',
          action: this.playMediaFile
        },
        scheduleCallback: {
          icon: 'mdi-calendar',
          label: 'buttons.scheduleCallback',
          action: this.scheduleCallback
        },
        changeDNC: {
          icon: 'mdi-alert-octagon',
          label:
            this.interaction.customer && !this.interaction.customer.dnc ? 'buttons.addToDNC' : 'buttons.removeFromDNC',
          action: this.changeDNC
        },
        accept: {
          icon: 'mdi-check',
          label: 'buttons.accept',
          action: this.acceptInteraction,
          customColor: 'var(--v-success-base) !important'
        },
        decline: {
          icon: 'mdi-cancel',
          label: 'buttons.decline',
          action: this.declineInteraction,
          waitsFor: ['INTERACTION_REMOVED_FROM_AGENT_EVENT'],
          customColor: 'var(--v-error-base) !important'
        },
        cancel: {
          icon: 'mdi-cancel',
          label: 'buttons.cancel',
          action: this.cancelInteraction,
          waitsFor: [INTERACTION_STATE.wrapUp]
        },
        call: {
          icon: 'mdi-phone',
          label: 'buttons.call',
          action: this.startCall,
          disabled: this.telephonyInteractionExists,
          waitsFor: ['INTERACTION_CREATED_OUTGOING_EVENT']
        },
        discard: {
          icon: 'mdi-cancel',
          label: 'buttons.discard',
          action: this.cancelInteraction,
          waitsFor: [INTERACTION_STATE.wrapUp]
        }
      };
    },

    primaryOptions() {
      if (!Object.keys(this.interaction).length) {
        return {};
      }

      const {
        isOnWrapUp,
        disposition: { dispositionCodeMandatory, dispositionCodeId }
      } = this.interaction;

      return {
        end: {
          icon: this.interaction.isChat
            ? 'mdi-message-bulleted-off'
            : this.interaction.isSms
            ? 'mdi-chat-remove'
            : 'mdi-phone-hangup',
          label: this.interaction.isChat
            ? 'buttons.endChat'
            : this.interaction.isSms
            ? 'buttons.endSms'
            : 'buttons.endCall',
          action: this.endInteraction,
          waitsFor: [INTERACTION_STATE.wrapUp]
        },
        complete: {
          icon: 'mdi-check',
          label: 'buttons.complete',
          action: this.completeInteraction,
          disabled: isOnWrapUp && dispositionCodeMandatory && !dispositionCodeId,
          waitsFor: ['INTERACTION_REMOVED_FROM_AGENT_EVENT']
        },
        delete: {
          icon: 'mdi-delete',
          label: 'buttons.deleteHistoric',
          action: this.deleteHistoricInteraction
        },
        send: {
          icon: 'mdi-send',
          label: 'buttons.send',
          action: this.sendEmail,
          disabled: !this.email.isFormValid || !this.email.attachmentsHaveValidSize,
          waitsFor: [INTERACTION_STATE.wrapUp]
        },
        reply: {
          icon: 'mdi-reply',
          label: 'buttons.reply',
          action: this.replyEmail,
          waitsFor: [ELEMENT_STATE.created]
        }
      };
    },

    elements() {
      return this.getElementsForInteraction(this.interaction).filter(element => !element.isEnded);
    },

    isHPBXAndAcceptEnabled() {
      return this.organizationData.enableIncomingCallRemoteAcceptOnPrimaryDevice && this.isHPBXPhone;
    }
  },

  methods: {
    ...mapActions('CONTACTS', ['updateCrmContact']),

    ...mapActions('EVENT_TIMING', [
      'clearEventTimingConstraint',
      'addEventTimingConstraint',
      'queueElementEventCallback'
    ]),

    ...mapActions('INTERACTIONS', [
      'editHistoricalInteraction',
      'editActiveInteraction',
      'createInteractionCrmContact',
      'removeHistoricalInteraction'
    ]),

    ...mapActions('INTERACTIONS/emails', ['setOutgoingEmailAsCanceled', 'setOutgoingEmailCreationTime']),

    ...mapActions('NOTIFICATIONS', ['clearUnreadMessagesNotifications']),

    ...mapActions('WINDOWS', ['setFocusedInteractionId', 'restoreInteractionWindow', 'setFocusedElementId']),

    cancelInteraction() {
      this.addEventTimingConstraint({
        interactionId: this.interaction.interactionId,
        constraintNames: [INTERACTION_STATE.wrapUp]
      });
      if (this.interaction.isEmail) {
        this.setOutgoingEmailAsCanceled(this.interaction.interactionId);
      }

      InteractionService.endInteraction(this.interaction.interactionId)
        .catch(() => this.refreshActiveInteractions())
        .finally(() => this.clearEventTimingConstraint(this.interaction));
    },

    getPostponeOptions() {
      const postponeOptions = (this.postponeOptions(this.interaction.routing.businessProcessId) || []).map(option => {
        return {
          text: splitCamelCasedWords(option.name),
          value: option.id,
          callback: () => this.postponeInteraction(this.interaction.interactionId, option.id)
        };
      });

      if (postponeOptions.length && !postponeOptions.map(o => o.value).includes(this.selectedPostponeId)) {
        this.selectedPostponeId = postponeOptions[0].value;
      }

      return postponeOptions;
    },

    getActionsOnPending() {
      const result = [];

      if (!this.interaction.isTelephony) {
        result.push(this.options.accept);
      }

      if (this.interaction.isCallback && !this.interaction.isPreview) {
        this.getPostponeOptions().forEach(option => {
          result.push({
            icon: 'mdi-calendar',
            label: option.text,
            action: () => this.postponeInteraction(option.value)
          });
        });
      }

      result.push(this.options.decline);

      return result;
    },

    getEmailOption(email) {
      if (this.interaction.isOutgoingEmail || (email && email.isReplying)) {
        return [this.options.cancel];
      } else {
        return [this.options.discard, this.options.transfer];
      }
    },

    insertChatOptions(optionsList) {
      if (this.interaction.isChat && !this.interaction.isSms) {
        if (this.elements[0].isOnHold) {
          optionsList.push(this.options.resume);
        } else if (!this.interaction.isConsultReceiver) {
          optionsList.push(this.options.hold);
          optionsList.push(this.options.scheduleCallback);
        }
      }

      if (!this.interaction.isConsultReceiver && !this.isDestinationOnInternalInteraction && !this.interaction.isSms) {
        if (this.interaction.isConference) {
          optionsList.push(this.options.splitConference);
        } else if (this.elements.length === 2) {
          optionsList.push(this.options.transferAttended);
          optionsList.push(this.options.conference);
        } else if (this.elements.length === 1) {
          optionsList.push(this.options.consult, this.options.transfer);
        }
      }
    },

    getActionsOnActive() {
      let result = [];

      if (this.elements.length && !this.interaction.isOnWrapUp) {
        // Email Options
        if (this.interaction.isEmail) {
          const email = this.getEmailByInteractionId(this.interaction.interactionId);
          // TODO: Need to understand why ECS destroys and recreates the elements on REPLY
          result = [...result, ...this.getEmailOption(email)];
          return result;
        }

        const businessProcess = this.getOrganizationBPbyId(this.interaction.routing.businessProcessId) || {};

        // Transfer to Auto VM is only available for external calls, callbacks, and previews
        if (
          this.interaction.isTransferable &&
          businessProcess.postHandlerBPAutoPlayVM &&
          (this.interaction.isExternalCall || this.interaction.isCallback || this.interaction.isCampaign)
        ) {
          result.push(this.options.transferToAutoVMService);
        }

        // Transfer to survey should be available for all interaction types except email
        if (
          this.interaction.isTransferable &&
          !this.interaction.isEmail &&
          businessProcess.postHandlerBPTransferToSurvey
        ) {
          result.push(this.options.transferToSurvey);
        }

        // SMS options
        if (this.interaction.isSms) {
          result.push(this.options.transfer);
          return result;
        }

        // Chat Options
        this.insertChatOptions(result);
      }

      if (this.interaction.isTelephony && !this.interaction.isInternal && !this.interaction.isConsultReceiver) {
        if (this.interaction.customer) {
          result.push(this.options.changeDNC);
        }

        if (!this.interaction.receptor.isInternalContact) {
          result.push(this.options.scheduleCallback);
        }
      }

      return result;
    },

    getActionsOnHistoric() {
      const result = [];

      if (this.interaction.isTelephony && !this.interaction.isInternal) {
        result.push(this.options.call);

        if (this.interaction.customer) {
          result.push(this.options.changeDNC);
        }

        if (!this.interaction.receptor.isInternalContact) {
          result.push(this.options.scheduleCallback);
        }
      }

      return result;
    },

    hold() {
      if (this.interaction.isDirectCall) {
        return this.holdDirectCallInteraction(this.interaction);
      }
      this.elements.forEach(element => {
        if (!element.isOnHold) {
          InteractionService.holdElement(this.interaction.interactionId, element.elementId)
            .catch(() => this.refreshActiveInteractions())
            .finally(() => this.clearEventTimingConstraint(this.interaction));
        }
      });
      this.addEventTimingConstraint({
        interactionId: this.interaction.interactionId,
        constraintNames: [this.interaction.isConference ? ELEMENT_STATE.conferencedDiverted : ELEMENT_STATE.diverted]
      });
    },

    resume() {
      if (this.interaction.isDirectCall) {
        return this.resumeDirectCallInteraction(this.interaction);
      }
      this.elements.forEach(element => {
        if (element.isOnHold) {
          InteractionService.resumeElement(this.interaction.interactionId, element.elementId)
            .catch(() => this.refreshActiveInteractions())
            .finally(() => this.clearEventTimingConstraint(this.interaction));
        }
      });
      this.addEventTimingConstraint({
        interactionId: this.interaction.interactionId,
        constraintNames: [this.interaction.isConference ? ELEMENT_STATE.conferenced : ELEMENT_STATE.active]
      });
    },

    openConsultDialog() {
      const consultToContact = contact => {
        InteractionService.startConsult(this.interaction.interactionId, contact.endPoint)
          .then(() => this.setFocusedInteractionId(this.interaction.interactionId))
          .catch(() => {
            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
              message: this.$t('snackbar.agentNotAvailable', { name: contact.name }),
              type: 'error'
            });
            this.refreshActiveInteractions();
          });
      };
      this.$refs.contactSelectionDialog
        .setDialogTitle(this.$t('dialogs.consultDialogTitle'))
        .setDialogSearchVerbiage(this.$t('dialogs.consultDialogSearchPlaceholder'))
        .setMediaType(this.interaction.interactionMediaType)
        .select()
        .then(consultToContact)
        .catch(console.log);
    },

    conference() {
      if (this.interaction.isChat) {
        this.$refs.groupChatSetupDialog
          .open()
          .then(this.startConference)
          .catch(console.log);
      } else {
        this.startConference();
      }
    },

    startConference(options = {}) {
      this.addEventTimingConstraint({
        interactionId: this.interaction.interactionId,
        constraintNames: [ELEMENT_STATE.conferenced]
      });

      const elementToTransfer = this.elements[0];
      const conversation = options.includeHistory
        ? this.getElementConversation(this.interaction.consultReceiver.id, elementToTransfer, true)
        : null;

      InteractionService.startConference(this.interaction.interactionId, conversation)
        .then(() => {
          this.setFocusedElementId(elementToTransfer.elementId);
          this.elements.forEach(({ elementId }) => {
            this.clearUnreadMessagesNotifications(elementId);
          });
        })
        .catch(() => {
          this.refreshActiveInteractions();
        })
        .finally(() => this.clearEventTimingConstraint(this.interaction));
    },

    getElementConversation(agentId, elementToTransfer, replaceConversation) {
      if (!this.interaction.isChat) {
        return;
      }

      const conversation = this.getConversationForElement(elementToTransfer);
      if (!conversation) {
        return;
      }

      const { messages } = conversation;
      return {
        agentId,
        interactionId: this.interaction.interactionId,
        messages: EcsConversation.purgeSystemMessages(messages),
        replaceConversation
      };
    },

    splitConference() {
      this.addEventTimingConstraint({
        interactionId: this.interaction.interactionId,
        constraintNames: [ELEMENT_STATE.diverted]
      });

      InteractionService.stopConference(this.interaction.interactionId)
        .catch(() => this.refreshActiveInteractions())
        .finally(() => {
          const elementToFocus = this.elements[0];
          this.setFocusedElementId(elementToFocus.elementId);
          this.clearEventTimingConstraint(this.interaction);
        });
    },

    openTransferDialog() {
      this.$refs.contactSelectionDialog
        .setDialogTitle(this.$t('dialogs.transferDialogTitle'))
        .setDialogSearchVerbiage(this.$t('dialogs.transferDialogSearchPlaceholder'))
        .setMediaType(this.interaction.interactionMediaType)
        .select()
        .then(this.transferToContact)
        .catch(console.log);
    },

    transferToContact(contact) {
      this.addEventTimingConstraint({
        interactionId: this.interaction.interactionId,
        constraintNames: ['INTERACTION_TRANSFERRED_EVENT']
      });

      const errorMessage = this.$t('snackbar.agentNotAvailable', { name: contact.name });
      InteractionService.transferToContact(this.interaction.interactionId, contact.endPoint)
        .then(() => {
          const successMessage = this.$t('snackbar.successfullyTransferredTo', {
            name: contact.name
          });

          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: successMessage,
            type: 'success'
          });
        })
        .catch(() => {
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: errorMessage,
            type: 'error'
          });
          this.refreshActiveInteractions();
        })
        .finally(() => {
          this.clearEventTimingConstraint(this.interaction);
        });
    },

    transferToSurvey() {
      this.addEventTimingConstraint({
        interactionId: this.interaction.interactionId,
        constraintNames: ['INTERACTION_TRANSFERRED_EVENT']
      });

      return InteractionService.transferToSurvey(this.interaction.interactionId)
        .then(() => {
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: this.$t('snackbar.successfullyTransferredToSurvey'),
            type: 'success'
          });
        })
        .catch(() => {
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: this.$t('snackbar.failedToTransferToSurvey'),
            type: 'error'
          });
          this.refreshActiveInteractions();
        })
        .finally(() => {
          this.clearEventTimingConstraint(this.interaction);
        });
    },

    transferToAutoVM() {
      this.addEventTimingConstraint({
        interactionId: this.interaction.interactionId,
        constraintNames: ['INTERACTION_TRANSFERRED_EVENT']
      });

      return InteractionService.transferToAutoVM(this.interaction.interactionId)
        .then(() => {
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: this.$t('snackbar.successfullyTransferredToAutoVM'),
            type: 'success'
          });
        })
        .catch(() => {
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: this.$t('snackbar.failedToTransferToAutoVM'),
            type: 'error'
          });
          this.refreshActiveInteractions();
        })
        .finally(() => {
          this.clearEventTimingConstraint(this.interaction);
        });
    },

    async transferAttended() {
      this.addEventTimingConstraint({
        interactionId: this.interaction.interactionId,
        constraintNames: ['INTERACTION_TRANSFERRED_EVENT']
      });

      let contact = this.getContact(this.interaction.consultReceiver.id);
      if (!contact) {
        contact = this.interaction.consultReceiver;
      }

      const elementToTransfer = this.elements[0];
      const receivingAgentElement = this.elements.length > 1 ? this.elements[1] : null;

      const transfer = () => {
        const successMessage = this.$t('snackbar.successfullyTransferredTo', { name: contact.name });
        InteractionService.transferAttended(
          this.interaction.interactionId,
          elementToTransfer.elementId,
          contact.endPoint
        )
          .then(() => {
            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
              message: successMessage,
              type: 'success'
            });

            this.forwardElementHistory(contact.id, elementToTransfer);
          })
          .catch(() => this.refreshActiveInteractions())
          .finally(() => this.clearEventTimingConstraint(this.interaction));
      };

      if (receivingAgentElement && receivingAgentElement.isOnHold) {
        await InteractionService.resumeElement(this.interaction.interactionId, receivingAgentElement.elementId);
        this.queueElementEventCallback({
          elementId: receivingAgentElement.elementId,
          eventName: 'ELEMENT_STATE_CHANGED_EVENT',
          callback: transfer
        });
        return;
      }

      transfer();
    },

    playMediaFile() {
      // TODO: Future functionality
    },

    scheduleCallback() {
      this.$root.$emit('open-new-callback-dialog', this.interaction);
    },

    changeDNC() {
      const submitToDNC = async isConfirmed => {
        if (!isConfirmed) {
          return;
        }

        let customer = this.interaction.customer;

        try {
          if (!customer.id) {
            customer = await this.createInteractionCrmContact(this.interaction).catch(error => {
              throw error;
            });
          }

          await this.updateCrmContact({
            contactId: customer.id,
            properties: { dnc: !customer.dnc }
          }).catch(error => {
            throw error;
          });

          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: this.$t(customer.dnc ? 'snackbar.removeFromDnc' : 'snackbar.addedToDnc', {
              name: this.interaction.crm.customerFullName || this.interaction.routing.origin
            }),
            type: 'success'
          });
        } catch {
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: this.$t(customer.dnc ? 'snackbar.failedToRemoveFromDnc' : 'snackbar.failedToAddToDnc', {
              name: this.interaction.crm.customerFullName || this.interaction.routing.origin
            }),
            type: 'error'
          });
        }
      };

      const confirmationDialog = this.interaction.customer.dnc
        ? this.$refs.removeFromDncConfirmationDialog
        : this.$refs.addToDncConfirmationDialog;
      confirmationDialog
        .open()
        .then(submitToDNC)
        .catch(console.log);
    },

    saveCompletionStatus() {
      if (!this.interaction.completionStatus || !this.interaction.completionStatus.completionStatusId) {
        return Promise.resolve();
      }
      return InteractionService.updateCompletionStatus(
        this.interaction.interactionId,
        this.interaction.completionStatus.completionStatusId
      );
    },

    saveCompletionStatusInformation() {
      if (!this.interaction.completionStatus || !this.interaction.completionStatus.completionStatusInformation) {
        return Promise.resolve();
      }

      const completionStatusInformation = {
        ...this.interaction.completionStatus.completionStatusInformation,
        pendingStatus: this.interaction.completionStatus.completionStatusName
      };
      return InteractionService.updateCompletionStatusInformation(
        this.interaction.interactionId,
        completionStatusInformation
      );
    },

    async completeInteraction() {
      this.addEventTimingConstraint({
        interactionId: this.interaction.interactionId,
        constraintNames: ['INTERACTION_REMOVED_FROM_AGENT_EVENT']
      });
      await this.saveCompletionStatus();
      await this.saveCompletionStatusInformation();

      // NOTE: The completion status information provides a pendingStatus distinct to handled
      // so is not required to end the interaction by second time to be handled.
      // The pendingStatus provided would be the handled state for interaction
      if (!this.interaction.completionStatus.completionStatusInformation) {
        InteractionService.endInteraction(this.interaction.interactionId)
          .catch(() => this.refreshActiveInteractions())
          .finally(() => this.clearEventTimingConstraint(this.interaction));
      }
    },

    endInteraction() {
      if (this.interaction.isDirectCall) {
        return this.endDirectCallInteraction(this.interaction);
      }
      this.addEventTimingConstraint({
        interactionId: this.interaction.interactionId,
        constraintNames: [INTERACTION_STATE.wrapUp]
      });
      const interactionWindow = this.getInteractionWindow(this.interaction.interactionId);
      if (interactionWindow.isClosed) {
        this.restoreInteractionWindow(this.interaction.interactionId);
      }
      const elementIds = this.getElementIdsByInteractionId(this.interaction.interactionId);
      elementIds.forEach(id => this.clearUnreadMessagesNotifications(id));
      InteractionService.endInteraction(this.interaction.interactionId)
        .catch(() => this.refreshActiveInteractions())
        .finally(() => this.clearEventTimingConstraint(this.interaction));
    },

    postponeInteraction(postponeOptionId) {
      const snackbarMessage = message => this.$root.$emit('snackbar-message', message);

      this.postponeLoading = true;
      InteractionService.postponeInteraction(this.interaction.interactionId, postponeOptionId)
        .catch(() => snackbarMessage({ message: `The operation could not be completed.`, type: 'error' }))
        .finally(() => (this.postponeLoading = false));
    },

    replyEmail() {
      this.addEventTimingConstraint({
        interactionId: this.interaction.interactionId,
        constraintNames: [ELEMENT_STATE.created]
      });
      InteractionService.replyEmail(this.interaction.interactionId)
        .catch(() => this.refreshActiveInteractions())
        .finally(() => this.clearEventTimingConstraint(this.interaction));
      const interactionWindow = this.getInteractionWindow(this.interaction.interactionId);
      if (interactionWindow.isClosed) {
        this.restoreInteractionWindow(this.interaction.interactionId);
      }
    },

    sendEmail() {
      this.addEventTimingConstraint({
        interactionId: this.interaction.interactionId,
        constraintNames: [INTERACTION_STATE.wrapUp]
      });
      const email = this.getEmailByInteractionId(this.interaction.interactionId);
      const requestBody = this.createEmailResponseBody(email);
      InteractionService.sendEmail(this.interaction.interactionId, requestBody)
        .then(
          sendEmailResult => {
            this.endInteraction();

            this.setOutgoingEmailCreationTime({
              interactionId: this.interaction.interactionId,
              date: moment().toISOString(true)
            });

            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
              message: this.$t('snackbar.successfullySendEmail'),
              type: 'success'
            });
          },
          () => {
            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
              message: this.$t('snackbar.failedToSendEmail'),
              type: 'error'
            });
          }
        )
        .catch(() => this.refreshActiveInteractions())
        .finally(() => this.clearEventTimingConstraint(this.interaction));
    },

    createEmailContent(email) {
      if (this.interaction.isOutgoingEmail) {
        return email.outgoingEmailData.body;
      }

      const separator = `
        <br>
        <div style="border-bottom: solid #CDCDCD 1px; width: 100%"></div>
        <br>
      `;
      return email.outgoingEmailData.body + separator + email.incomingEmailData.body;
    },

    getFileAttachments(attachments) {
      return attachments.map(attachment => attachment.file);
    },

    createEmailResponseBody(email) {
      const bodyHtml = this.createEmailContent(email);
      // Adding required parameters
      const response = {
        toAddress: email.outgoingEmailData.to,
        subject: email.outgoingEmailData.subject,
        bodyHtml,
        formData: true
      };
      // Adding optional parameters
      if (this.isSmtpEnabled) {
        response.fromAddress = email.outgoingEmailData.from;
      }
      if (email.outgoingEmailData.cc !== '') {
        response.ccAddress = email.outgoingEmailData.cc;
      }
      if (email.outgoingEmailData.requiredBcc !== '') {
        response.bccAddress = `${email.outgoingEmailData.requiredBcc};${email.outgoingEmailData.bcc}`;
      } else if (email.outgoingEmailData.bcc !== '') {
        response.bccAddress = email.outgoingEmailData.bcc;
      }
      if (email.outgoingEmailData.attachments.length) {
        response.attachments = this.getFileAttachments(email.outgoingEmailData.attachments);
      }

      return response;
    },

    deleteHistoricInteraction() {
      this.removeHistoricalInteraction(this.interaction);
    },

    acceptInteraction() {
      InteractionService.acceptInteraction(this.interaction.interactionId)
        .then(() => this.setFocusedInteractionId(this.interaction.interactionId))
        .catch(() => this.refreshActiveInteractions())
        .finally(() => this.clearEventTimingConstraint(this.interaction));
    },

    declineInteraction() {
      InteractionService.declineInteraction(this.interaction.interactionId)
        .catch(() => this.refreshActiveInteractions())
        .finally(() => this.clearEventTimingConstraint(this.interaction));
    },

    startCall() {
      this.addEventTimingConstraint({
        interactionId: this.interaction.interactionId,
        constraintNames: ['INTERACTION_CREATED_OUTGOING_EVENT']
      });
      const businessProcessId = this.interaction.routing.businessProcessId;
      const name = this.interaction.crm.customerFullName;

      let phone = this.interaction.routing.origin;

      switch (this.interaction.interactionType.toUpperCase()) {
        case EcsInteraction.interactionTypes.callbackTelephony:
        case EcsInteraction.interactionTypes.externalCall:
          phone = this.interaction.routing.destination;
          break;
      }

      const errorMessage = `${this.$t('snackbar.failedToCall', { name, phone })}`;

      if (!phone) {
        this.emitErrorMessage(errorMessage);
        return;
      }

      if (!this.agentState.isBreak && !businessProcessId) {
        this.emitErrorMessage(errorMessage);
        return;
      }

      const telephonyBusinessProcesses = this.availableBusinessProcesses
        .filter(bp => bp.outgoingMediaTypes.includes('Telephony'))
        .reduce((accum, current) => accum.concat(current.id), []);

      if (!telephonyBusinessProcesses.includes(businessProcessId)) {
        this.$root.$emit('open-dialpad-number', { number: phone });
        return;
      }

      const interactionData = {
        businessProcessId: this.agentState.isBreak ? undefined : businessProcessId,
        interaction: {
          destination: phone,
          fullName: name,
          isPrivateInteraction: this.agentState.isBreak
        }
      };
      InteractionService.startTelephonyInteraction(interactionData)
        .then(response => {
          if (response.status === STATUS_SUCCESS) {
            if (this.$router.currentRoute.name !== 'Calls') {
              this.$router.push('calls').catch(() => {});
            }
          } else {
            this.emitErrorMessage(errorMessage);
          }
        })
        .catch(() => {
          this.emitErrorMessage(errorMessage);
          this.refreshActiveInteractions();
        })
        .finally(() => {
          this.clearEventTimingConstraint(this.interaction);
        });
    },
    emitErrorMessage(message) {
      this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
        message,
        type: 'error'
      });
    },
    refreshActiveInteractions() {
      this.$root.$emit('restore-active-interactions');
    }
  }
};
